import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationAdminDashboard } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { formatDateToUI } from '@dealstack/shared/lib/format/dates/date';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { Badge, Divider, Flex, Text } from '@mantine/core';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import type { DealCardProps } from './DealCard.types';

export const DealCard = ({ deal, to }: DealCardProps) => {
	const tAdminDashboard = useTranslationAdminDashboard();
	const isRestrictedThroughSupportAccess =
		deal.isRestricted && deal.isVisibleThroughDealStackSupportAccess;

	const activeMipsCount = useMemo(
		() => deal.mips?.filter((ip) => ip.state === 'ACTIVE')?.length,
		[deal.mips]
	);
	const draftMipCount = deal.mips?.length - activeMipsCount;

	return (
		<StyledCard
			to={to}
			$isRestricted={isRestrictedThroughSupportAccess}
			data-testid={TEST_ELEMENT_IDS.ADMIN_DASHBOARD_PAGE_DEAL_BLOCK}
		>
			<Flex direction="column" gap="0.25rem">
				<Text size="B6Bold">{tAdminDashboard('v3.deal_card.deal_name')}</Text>
				<Text lineClamp={2} truncate size="B4Light">
					{deal.name}
				</Text>
			</Flex>
			<Flex direction="column" gap="0.25rem">
				<Text size="B6Bold">{tAdminDashboard('v3.deal_card.target_company')}</Text>
				<Text lineClamp={2} truncate size="B4Light">
					{deal.targetName}
				</Text>
			</Flex>

			<Flex gap="0.5rem" mb="1rem" wrap="wrap">
				{activeMipsCount ? (
					<Badge
						variant="filled"
						color="teal.2"
						c="teal.9"
						leftSection={
							<Icon
								variant="plant-grown"
								style={{
									width: '0.875rem',
									height: '0.875rem',
									marginRight: '0.125rem',
								}}
							/>
						}
					>
						<Text size="B6Medium">
							{tAdminDashboard('v3.deal_card.active_programme', {
								count: activeMipsCount,
							})}
						</Text>
					</Badge>
				) : null}
				{draftMipCount ? (
					<Badge
						variant="filled"
						color="yellow.2"
						c="yellow.9"
						leftSection={
							<Icon
								variant="plant-grown"
								style={{
									width: '0.875rem',
									height: '0.875rem',
									marginRight: '0.125rem',
								}}
							/>
						}
					>
						<Text size="B6Medium">
							{tAdminDashboard('v3.deal_card.draft_programme', {
								count: draftMipCount,
							})}
						</Text>
					</Badge>
				) : null}
				<Badge
					variant="filled"
					color="teal.2"
					c="teal.9"
					leftSection={<Icon variant="tick" size="lg" />}
				>
					<Text size="B6Medium">{tAdminDashboard('v3.deal_card.cap_table')}</Text>
				</Badge>
				{isRestrictedThroughSupportAccess ? (
					<Badge variant="light" color="red" data-testid="deal-restricted-badge">
						<Text size="B6Medium">
							{tAdminDashboard('v3.deal_card.restricted', {
								defaultValue: 'Restricted',
							})}
						</Text>
					</Badge>
				) : null}
			</Flex>

			<Divider mt="auto" />
			<Flex justify="space-between">
				<Flex gap="0.5rem">
					<Icon size="lg" variant="calendar-outlined" />
					<Text size="B6Medium">
						{tAdminDashboard('v3.deal_card.created_on')}{' '}
						{formatDateToUI(new Date(deal?.createdAt))}
					</Text>
				</Flex>
			</Flex>
		</StyledCard>
	);
};

const StyledCard = styled(Link)<{ $isRestricted?: boolean }>`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	gap: 1rem;
	padding: 1rem;
	background-color: ${({ theme }) => theme.v2.color.neutral[100]};
	border-radius: 0.5rem;
	min-width: 27rem;
	min-height: 17.5rem;
	cursor: ${({ $isRestricted }) => ($isRestricted ? 'default' : 'pointer')};
	color: inherit;
	text-decoration: none;
	pointer-events: ${({ $isRestricted }) => ($isRestricted ? 'none' : 'auto')};

	&:hover {
		color: inherit;
		text-decoration: none;
	}

	.mantine-Text-root {
		white-space: nowrap;
	}
`;
